import { GatewayAPIClient, GatewayAPIError, gatewayAPIClient } from "../../app/api/client";

export type DiscoveryKind = "user" | "group" | "channel";

export interface DiscoveryUserResult {
  kind: "user";
  user_id: string;
  display_name: string;
  nickname?: string;
  username?: string;
  status_text?: string;
  avatar_url?: string;
}

export interface DiscoveryConversationResult {
  kind: "group" | "channel";
  chat_id: string;
  name: string;
  description?: string;
  avatar_asset_id?: string;
  member_count: number;
  participation: "none" | "member" | "admin" | "publisher" | "subscriber";
}

export type DiscoveryResult = DiscoveryUserResult | DiscoveryConversationResult;

type DiscoveryAPIClient = Pick<GatewayAPIClient, "request">;

interface DiscoverySearchResponse {
  results: unknown[];
}

const DISCOVERY_PARTICIPATIONS = new Set<DiscoveryConversationResult["participation"]>([
  "none",
  "member",
  "admin",
  "publisher",
  "subscriber",
]);

function getClient(client?: DiscoveryAPIClient): DiscoveryAPIClient {
  return client ?? gatewayAPIClient;
}

function invalidDiscoveryResponse(details: unknown): GatewayAPIError {
  return new GatewayAPIError(500, "invalid_response", details);
}

function buildPath(basePath: string, query: URLSearchParams): string {
  const encoded = query.toString();
  if (encoded === "") {
    return basePath;
  }
  return `${basePath}?${encoded}`;
}

function normalizeQuery(value: string): string {
  const normalized = value.trim();
  if (normalized === "") {
    throw new Error("query must not be empty");
  }
  return normalized;
}

function normalizeIdentifier(value: string, fieldName: string): string {
  const normalized = value.trim();
  if (normalized === "") {
    throw new Error(`${fieldName} must not be empty`);
  }
  if (normalized.includes("/")) {
    throw new Error(`${fieldName} contains invalid path separator`);
  }
  if (/\s/.test(normalized)) {
    throw new Error(`${fieldName} contains invalid whitespace`);
  }
  return normalized;
}

function normalizeUserID(value: unknown, payload: unknown): string {
  if (typeof value !== "string") {
    throw invalidDiscoveryResponse(payload);
  }
  const normalized = normalizeIdentifier(value, "user_id");
  if (!normalized.startsWith("usr_")) {
    throw invalidDiscoveryResponse(payload);
  }
  return normalized;
}

function objectFromPayload(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw invalidDiscoveryResponse(value);
  }
  return value as Record<string, unknown>;
}

function stringFromPayload(value: unknown, payload: unknown): string {
  if (typeof value !== "string") {
    throw invalidDiscoveryResponse(payload);
  }
  return value.trim();
}

function optionalStringFromPayload(value: unknown, payload: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value !== "string") {
    throw invalidDiscoveryResponse(payload);
  }
  const normalized = value.trim();
  return normalized === "" ? undefined : normalized;
}

function normalizeSearchResponse(payload: unknown): DiscoverySearchResponse {
  const response = objectFromPayload(payload);
  if (!Array.isArray(response.results)) {
    throw invalidDiscoveryResponse(payload);
  }
  return {
    results: response.results,
  };
}

function emptySearchResponse(): DiscoverySearchResponse {
  return { results: [] };
}

function shouldQueryUserDiscovery(value: string): boolean {
  const normalized = value.trim();
  if (normalized.startsWith("usr_")) {
    return true;
  }
  if (normalized.includes("@")) {
    return true;
  }

  const phoneCandidate = normalized.replace(/[\s().-]/g, "");
  if (/^\+?\d{8,15}$/.test(phoneCandidate) || /^00\d{6,13}$/.test(phoneCandidate)) {
    return true;
  }

  const nicknameCandidate = normalized.startsWith("@") ? normalized.slice(1) : normalized;
  const nicknameLength = Array.from(nicknameCandidate).length;
  return nicknameLength >= 3 && nicknameLength <= 32 && /^[\p{L}\p{N}_.]+$/u.test(nicknameCandidate);
}

function isIgnorableUserDiscoveryError(error: unknown): boolean {
  return error instanceof GatewayAPIError && error.status === 400 && error.code === "invalid_request";
}

async function searchDiscoveryUsers(
  apiClient: DiscoveryAPIClient,
  query: URLSearchParams,
): Promise<unknown> {
  try {
    return await apiClient.request<unknown>({
      method: "GET",
      path: buildPath("/v1/discovery/users", query),
    });
  } catch (error) {
    if (isIgnorableUserDiscoveryError(error)) {
      return emptySearchResponse();
    }
    throw error;
  }
}

function normalizeUserResult(payload: unknown): DiscoveryUserResult {
  const item = objectFromPayload(payload);
  if (item.kind !== "user") {
    throw invalidDiscoveryResponse(payload);
  }

  return {
    kind: "user",
    user_id: normalizeUserID(item.user_id, payload),
    display_name: stringFromPayload(item.display_name, payload),
    nickname: optionalStringFromPayload(item.nickname, payload),
    username: optionalStringFromPayload(item.username, payload),
    status_text: optionalStringFromPayload(item.status_text, payload),
    avatar_url: optionalStringFromPayload(item.avatar_url, payload),
  };
}

function normalizeConversationResult(payload: unknown): DiscoveryConversationResult {
  const item = objectFromPayload(payload);
  if (item.kind !== "group" && item.kind !== "channel") {
    throw invalidDiscoveryResponse(payload);
  }
  const memberCount = item.member_count;
  if (typeof memberCount !== "number" || !Number.isInteger(memberCount) || memberCount < 0) {
    throw invalidDiscoveryResponse(payload);
  }
  const participation = item.participation;
  if (typeof participation !== "string" || !DISCOVERY_PARTICIPATIONS.has(participation as never)) {
    throw invalidDiscoveryResponse(payload);
  }

  return {
    kind: item.kind,
    chat_id: normalizeIdentifier(stringFromPayload(item.chat_id, payload), "chat_id"),
    name: stringFromPayload(item.name, payload),
    description: optionalStringFromPayload(item.description, payload),
    avatar_asset_id: optionalStringFromPayload(item.avatar_asset_id, payload),
    member_count: memberCount,
    participation: participation as DiscoveryConversationResult["participation"],
  };
}

function stableKindMerge(
  userPayload: unknown,
  conversationPayload: unknown,
): DiscoveryResult[] {
  const users = normalizeSearchResponse(userPayload).results.map(normalizeUserResult);
  const conversations = normalizeSearchResponse(conversationPayload).results.map(normalizeConversationResult);
  const groups = conversations.filter((result) => result.kind === "group");
  const channels = conversations.filter((result) => result.kind === "channel");
  return [...users, ...groups, ...channels];
}

export async function searchDiscovery(query: string, client?: DiscoveryAPIClient): Promise<DiscoveryResult[]> {
  const normalizedQuery = normalizeQuery(query);
  const usersQuery = new URLSearchParams();
  usersQuery.set("q", normalizedQuery);
  const conversationsQuery = new URLSearchParams();
  conversationsQuery.set("q", normalizedQuery);
  conversationsQuery.set("type", "all");

  const apiClient = getClient(client);
  const userPayloadPromise = shouldQueryUserDiscovery(normalizedQuery)
    ? searchDiscoveryUsers(apiClient, usersQuery)
    : Promise.resolve(emptySearchResponse());
  const [userPayload, conversationPayload] = await Promise.all([
    userPayloadPromise,
    apiClient.request<unknown>({
      method: "GET",
      path: buildPath("/v1/discovery/conversations", conversationsQuery),
    }),
  ]);

  return stableKindMerge(userPayload, conversationPayload);
}

export async function joinDiscoveredGroup(groupID: string, client?: DiscoveryAPIClient): Promise<void> {
  const normalizedGroupID = normalizeIdentifier(groupID, "group_id");
  await getClient(client).request<void>({
    method: "POST",
    path: `/v1/discovery/groups/${encodeURIComponent(normalizedGroupID)}/join`,
  });
}

export async function subscribeDiscoveredChannel(channelID: string, client?: DiscoveryAPIClient): Promise<void> {
  const normalizedChannelID = normalizeIdentifier(channelID, "channel_id");
  await getClient(client).request<void>({
    method: "POST",
    path: `/v1/discovery/channels/${encodeURIComponent(normalizedChannelID)}/subscribe`,
  });
}
